import { useCallback } from "react";
import type { EdgeData, NodeData } from "../lib/api";

export function useGraphMapping(hideInactive: boolean) {
  const edgeWithMarkers = useCallback((edgeList: any[]) => {
    return edgeList.map((e) => {
      const data = (e.data as EdgeData) || ({} as EdgeData);
      const arrowStyle = data.arrow_style || "default";
      const edgeColor = (data as any)?.color || "#64748b";
      const lineStyle = (data as any)?.line_style || "bezier";

      const markerStart =
        arrowStyle === "reverse" || arrowStyle === "both"
          ? { type: "arrowclosed" as const, color: edgeColor }
          : undefined;
      const markerEnd =
        arrowStyle === "default" || arrowStyle === "both"
          ? { type: "arrowclosed" as const, color: edgeColor }
          : undefined;

      const strokeDasharray = (data as any)?.style === "dashed" ? "6 6" : undefined;

      let edgeType = "default";
      if (lineStyle === "straight") edgeType = "straight";
      else if (lineStyle === "step") edgeType = "orthogonal";
      else if (lineStyle === "smoothstep") edgeType = "smoothstep";

      const routing = data.routing || { mode: "auto", bendOffset: 0 };

      return {
        ...e,
        type: edgeType,
        data: { ...data, routing },
        markerStart,
        markerEnd,
        style: {
          ...e.style,
          stroke: edgeColor,
          strokeWidth: 2,
          strokeDasharray,
        },
      };
    });
  }, []);

  const applyVisibility = useCallback(
    (nodes: any[], edges: any[]) => {
      const childrenMap = new Map<string, string[]>();

      edges.forEach((e) => {
        const arrowStyle = (e.data as EdgeData)?.arrow_style || "default";

        if (arrowStyle === "reverse") {
          if (!childrenMap.has(e.target)) childrenMap.set(e.target, []);
          childrenMap.get(e.target)?.push(e.source);
        } else if (arrowStyle === "default" || arrowStyle === "both") {
          if (!childrenMap.has(e.source)) childrenMap.set(e.source, []);
          childrenMap.get(e.source)?.push(e.target);

          if (arrowStyle === "both") {
            if (!childrenMap.has(e.target)) childrenMap.set(e.target, []);
            childrenMap.get(e.target)?.push(e.source);
          }
        }
      });

      const collapsedNodeIds = new Set<string>();
      nodes.forEach((n) => {
        const data = n.data as NodeData;
        if (data.collapsed) {
          collapsedNodeIds.add(n.id);
        }
      });

      const hiddenByCollapse = new Set<string>();
      const descendantCounts = new Map<string, number>();

      collapsedNodeIds.forEach((collapsedId) => {
        let count = 0;
        const visited = new Set<string>();
        const queue = [...(childrenMap.get(collapsedId) || [])];

        while (queue.length > 0) {
          const nodeId = queue.shift()!;
          if (visited.has(nodeId)) continue;
          visited.add(nodeId);

          hiddenByCollapse.add(nodeId);
          count++;

          const children = childrenMap.get(nodeId) || [];
          children.forEach((child) => {
            if (!visited.has(child)) {
              queue.push(child);
            }
          });
        }

        descendantCounts.set(collapsedId, count);
      });

      const mappedNodes = nodes.map((n) => {
        const data = n.data as NodeData;
        const hidden = hiddenByCollapse.has(n.id) || (hideInactive && data.inactive);
        const hiddenDescendantCount = descendantCounts.get(n.id) || 0;
        return { ...n, hidden, data: { ...data, hiddenDescendantCount } };
      });

      const mappedEdges = edges.map((e) => ({
        ...e,
        hidden: hiddenByCollapse.has(e.source) || hiddenByCollapse.has(e.target),
      }));

      return { nodes: mappedNodes, edges: mappedEdges };
    },
    [hideInactive]
  );

  const mapGraphState = useCallback(
    (nodes: any[], edges: any[]) => {
      const edgesWithMarkers = edgeWithMarkers(edges);
      return applyVisibility(nodes, edgesWithMarkers);
    },
    [applyVisibility, edgeWithMarkers]
  );

  return { mapGraphState };
}
